import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  MessageCircle,
  QrCode,
  Clock,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Wifi,
  WifiOff,
  Send,
  Loader2,
  Inbox,
  XCircle,
  ArrowUpCircle,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAuthAxios, useAuthStore } from '@/store';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useI18n } from '@/components/I18nProvider';

const formatDate = (d) => {
  if (!d) return '-';
  try {
    const date = new Date(d);
    return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '-';
  }
};

export default function WhatsAppPage() {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const [status, setStatus] = useState('disconnected');
  const [qrCode, setQrCode] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendForm, setSendForm] = useState({ phone: '', message: '' });
  const [pollInterval, setPollInterval] = useState(null);
  const [msgStats, setMsgStats] = useState({ total_sent: 0, total_failed: 0, today_sent: 0, today_failed: 0, pending_in_queue: 0 });
  const [messages, setMessages] = useState([]);
  const [msgTab, setMsgTab] = useState('all');
  const [msgLoading, setMsgLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';

  const handleDeleteMessage = async (msgId) => {
    if (!window.confirm('Bu mesajı silmek istediğinizden emin misiniz?')) return;
    try {
      const api = getAuthAxios();
      await api.delete(`/whatsapp/messages/${msgId}`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
      toast.success('Mesaj silindi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mesaj silinemedi');
    }
  };

  const fetchStatus = useCallback(async () => {
    try {
      const api = getAuthAxios();
      const response = await api.get('/whatsapp/status');
      const data = response.data;
      setStatus(data.status);
      setQrCode(data.qr);
      if (data.status === 'connected' && pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    } catch (error) {
      console.error('Status fetch error:', error);
    }
  }, [pollInterval]);

  const fetchMsgData = useCallback(async () => {
    try {
      const api = getAuthAxios();
      const [statsRes, msgsRes] = await Promise.all([
        api.get('/whatsapp/message-stats'),
        api.get('/whatsapp/messages?limit=50'),
      ]);
      setMsgStats(statsRes.data);
      setMessages(msgsRes.data);
    } catch (error) {
      console.error('Message data fetch error:', error);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchMsgData();
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const api = getAuthAxios();
      const response = await api.post('/whatsapp/connect');
      const data = response.data;
      if (data.status === 'connected') {
        setStatus('connected');
        setQrCode(null);
        toast.success('WhatsApp bağlandı!');
      } else if (data.qr) {
        setQrCode(data.qr);
        setStatus('waiting_for_scan');
        toast.info('QR kodu telefonunuzla tarayın');
        const interval = setInterval(fetchStatus, 3000);
        setPollInterval(interval);
      } else {
        toast.info('QR kod oluşturuluyor, lütfen bekleyin...');
        setTimeout(async () => {
          const retryResponse = await api.get('/whatsapp/status');
          if (retryResponse.data.qr) {
            setQrCode(retryResponse.data.qr);
            setStatus('waiting_for_scan');
            const interval = setInterval(fetchStatus, 3000);
            setPollInterval(interval);
          }
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bağlantı hatası');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const api = getAuthAxios();
      await api.post('/whatsapp/disconnect');
      setStatus('disconnected');
      setQrCode(null);
      toast.success('WhatsApp bağlantısı kesildi');
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bağlantı kesme hatası');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!sendForm.phone || !sendForm.message) {
      toast.error('Telefon ve mesaj gerekli');
      return;
    }
    setIsSending(true);
    try {
      const api = getAuthAxios();
      const response = await api.post('/whatsapp/send', sendForm);
      if (response.data.success) {
        toast.success('Mesaj kuyruğa alındı!');
        setSendForm({ phone: '', message: '' });
        fetchMsgData();
      } else {
        toast.error(response.data.error || t('messageFailed'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mesaj gönderme hatası');
    } finally {
      setIsSending(false);
    }
  };

  const handleRetryOne = async (msgId) => {
    try {
      const api = getAuthAxios();
      await api.post(`/whatsapp/retry/${msgId}`);
      toast.success('Mesaj tekrar kuyruğa alındı');
      fetchMsgData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Tekrar gönderme hatası');
    }
  };

  const handleRetryAll = async () => {
    setIsRetrying(true);
    try {
      const api = getAuthAxios();
      const res = await api.post('/whatsapp/retry-all');
      toast.success(res.data.message || 'Tüm hatalı mesajlar tekrar kuyruğa alındı');
      fetchMsgData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toplu tekrar gönderme hatası');
    } finally {
      setIsRetrying(false);
    }
  };

  const filteredMessages = messages.filter((m) => {
    if (msgTab === 'all') return true;
    if (msgTab === 'sent') return m.status === 'sent';
    if (msgTab === 'failed') return m.status === 'failed';
    if (msgTab === 'pending') return m.status === 'pending';
    return true;
  });

  const isConnected = status === 'connected';
  const isWaitingForScan = status === 'waiting_for_scan';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="whatsapp-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">WhatsApp</h1>
          <p className="mt-1 text-sm text-muted-foreground">Bağlantı ve mesaj yönetimi</p>
        </div>
        <Badge
          variant="outline"
          className={`gap-2 px-3 py-1 ${isConnected ? 'bg-green-500 text-white border-green-500' : 'bg-gray-100 text-gray-600 border-gray-300'}`}
          data-testid="connection-status"
        >
          {isConnected ? <><Wifi className="h-4 w-4" />{t('connected')}</> : <><WifiOff className="h-4 w-4" />{t('disconnected')}</>}
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="shrink-0 rounded-lg bg-whatsapp/10 p-2"><MessageCircle className="h-4 w-4 sm:h-5 sm:w-5 text-whatsapp-dark" /></div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">{t('status')}</p>
                <p className="font-heading text-sm sm:text-base font-bold truncate">{isConnected ? t('connected') : isWaitingForScan ? 'QR Bekliyor' : t('disconnected')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="shrink-0 rounded-lg bg-amber-100 p-2"><Clock className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" /></div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">Kuyrukta</p>
                <p className="font-heading text-sm sm:text-base font-bold">{msgStats.pending_in_queue}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="shrink-0 rounded-lg bg-green-100 p-2"><CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" /></div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">Gönderilen</p>
                <p className="font-heading text-sm sm:text-base font-bold truncate">{msgStats.total_sent} <span className="text-[10px] sm:text-xs font-normal text-muted-foreground">({msgStats.today_sent})</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="shrink-0 rounded-lg bg-red-100 p-2"><XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" /></div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">Hatalı</p>
                <p className="font-heading text-sm sm:text-base font-bold truncate">{msgStats.total_failed} <span className="text-[10px] sm:text-xs font-normal text-muted-foreground">({msgStats.today_failed})</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Connection + How To */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              {isConnected ? <CheckCircle className="h-5 w-5 text-whatsapp" /> : <QrCode className="h-5 w-5 text-primary" />}
              {isConnected ? 'WhatsApp Durumu' : 'QR Kod ile Bağlan'}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            {isConnected ? (
              <div className="flex flex-col items-center gap-4 py-6">
                <div className="rounded-full bg-status-paid/10 p-4"><CheckCircle className="h-12 w-12 text-status-paid" /></div>
                <p className="text-lg font-medium text-status-paid">WhatsApp Bağlı</p>
                <p className="text-sm text-muted-foreground text-center">Sistem aktif, mesajlar gönderilebilir</p>
                {isAdmin && (
                  <Button variant="outline" onClick={handleDisconnect} disabled={isDisconnecting} data-testid="disconnect-btn">
                    {isDisconnecting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Kesiliyor...</> : t('disconnect')}
                  </Button>
                )}
              </div>
            ) : isAdmin ? (
              <div className="flex flex-col items-center gap-4">
                {qrCode ? (
                  <div className="rounded-xl border-4 border-whatsapp/20 p-4 bg-white">
                    <img src={qrCode} alt="QR Code" className="h-48 w-48 sm:h-64 sm:w-64" data-testid="qr-code-image" />
                  </div>
                ) : (
                  <div className="flex h-48 w-48 sm:h-64 sm:w-64 items-center justify-center rounded-xl border-4 border-dashed border-muted">
                    <p className="text-center text-sm text-muted-foreground">QR kod oluşturmak için<br />butona tıklayın</p>
                  </div>
                )}
                {isWaitingForScan && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <RefreshCw className="h-4 w-4 animate-spin" />Tarama bekleniyor...
                  </div>
                )}
                <Button onClick={handleConnect} disabled={isConnecting} data-testid="connect-whatsapp-btn" className="bg-whatsapp hover:bg-whatsapp-dark">
                  {isConnecting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Bağlanıyor...</> : qrCode ? <><RefreshCw className="mr-2 h-4 w-4" />Yeni QR Kod</> : <><QrCode className="mr-2 h-4 w-4" />QR Kod Oluştur</>}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 py-6">
                <div className="rounded-full bg-destructive/10 p-4"><WifiOff className="h-12 w-12 text-destructive" /></div>
                <p className="text-lg font-medium text-destructive">WhatsApp Bağlı Değil</p>
                <p className="text-sm text-muted-foreground text-center">WhatsApp bağlantısı için firma yöneticinize başvurun</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Send Message / How To */}
        {isConnected ? (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Send className="h-5 w-5 text-whatsapp" />Test Mesajı Gönder
              </CardTitle>
              <CardDescription>Bağlantıyı test etmek için mesaj gönderin</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendMessage} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon Numarası</Label>
                  <Input id="phone" placeholder="905xxxxxxxxx" value={sendForm.phone} onChange={(e) => setSendForm({ ...sendForm, phone: e.target.value })} data-testid="send-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">{t('messageText')}</Label>
                  <Textarea id="message" placeholder="Mesajınızı yazın..." value={sendForm.message} onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })} rows={3} data-testid="send-message-input" />
                </div>
                <Button type="submit" disabled={isSending || !sendForm.phone || !sendForm.message} className="w-full bg-whatsapp hover:bg-whatsapp-dark" data-testid="send-message-btn">
                  {isSending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Gönderiliyor...</> : <><Send className="mr-2 h-4 w-4" />Mesaj Gönder</>}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Nasıl Bağlanılır?</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-4">
                {[
                  { t: 'WhatsApp\'ı Açın', d: 'Telefonunuzda WhatsApp uygulamasını açın' },
                  { t: 'Bağlı Cihazlar', d: 'Ayarlar > Bağlı Cihazlar menüsüne gidin' },
                  { t: t('connectDevice'), d: '"Cihaz Bağla" butonuna tıklayın' },
                  { t: 'QR Kodu Tarayın', d: 'Sol taraftaki QR kodu tarayın' },
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-whatsapp text-sm font-medium text-white">{i + 1}</span>
                    <div>
                      <p className="font-medium">{step.t}</p>
                      <p className="text-sm text-muted-foreground">{step.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Message Queue / Logs */}
      <Card data-testid="message-queue-section">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Inbox className="h-5 w-5 text-primary" />
              Mesaj Kuyruğu
            </CardTitle>
            <div className="flex items-center gap-2">
              {messages.filter(m => m.status === 'failed').length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryAll}
                  disabled={isRetrying}
                  className="text-xs gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="retry-all-btn"
                >
                  {isRetrying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Hepsini Tekrar Gönder
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={fetchMsgData} data-testid="refresh-messages-btn">
                <RefreshCw className="mr-1.5 h-4 w-4" />Yenile
              </Button>
            </div>
          </div>
          <CardDescription>Gönderilen, bekleyen ve hatalı mesajları görüntüleyin</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={msgTab} onValueChange={setMsgTab}>
            <TabsList className="grid w-full max-w-lg grid-cols-4">
              <TabsTrigger value="all" className="gap-1 text-xs sm:text-sm" data-testid="msg-tab-all">
                <Inbox className="h-3.5 w-3.5 hidden sm:inline" />
                Tümü ({messages.length})
              </TabsTrigger>
              <TabsTrigger value="pending" className="gap-1 text-xs sm:text-sm" data-testid="msg-tab-pending">
                <Clock className="h-3.5 w-3.5 hidden sm:inline" />
                Bekleyen ({messages.filter(m => m.status === 'pending').length})
              </TabsTrigger>
              <TabsTrigger value="sent" className="gap-1 text-xs sm:text-sm" data-testid="msg-tab-sent">
                <CheckCircle className="h-3.5 w-3.5 hidden sm:inline" />
                Giden ({messages.filter(m => m.status === 'sent').length})
              </TabsTrigger>
              <TabsTrigger value="failed" className="gap-1 text-xs sm:text-sm" data-testid="msg-tab-failed">
                <XCircle className="h-3.5 w-3.5 hidden sm:inline" />
                Hata ({messages.filter(m => m.status === 'failed').length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value={msgTab} className="mt-4">
              {filteredMessages.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  <Inbox className="mx-auto mb-3 h-10 w-10 opacity-30" />
                  <p>Bu kategoride mesaj yok</p>
                </div>
              ) : (
                <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
                  {filteredMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={cn(
                        'rounded-lg border p-3 transition-all',
                        msg.status === 'sent' && 'bg-green-50/50 border-green-200/60',
                        msg.status === 'failed' && 'bg-red-50/50 border-red-200/60',
                        msg.status === 'pending' && 'bg-amber-50/50 border-amber-200/60'
                      )}
                      data-testid={`msg-item-${msg.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">{msg.cari_title || msg.phone}</span>
                            {msg.cari_title && <span className="text-xs text-muted-foreground">{msg.phone}</span>}
                            <Badge
                              variant="outline"
                              className={cn(
                                'text-[10px] px-1.5 py-0',
                                msg.status === 'sent' && 'border-green-300 text-green-700 bg-green-100',
                                msg.status === 'failed' && 'border-red-300 text-red-700 bg-red-100',
                                msg.status === 'pending' && 'border-amber-300 text-amber-700 bg-amber-100'
                              )}
                            >
                              {msg.status === 'sent' ? 'Gönderildi' : msg.status === 'failed' ? 'Hata' : 'Beklemede'}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                              {msg.message_type === 'manual' ? 'Manuel' : msg.message_type === 'reminder' ? 'Hatırlatma' : msg.message_type === 'payment_created' ? 'Ödeme Bildirimi' : msg.message_type}
                            </Badge>
                            {msg.retry_count > 0 && (
                              <span className="text-[10px] text-muted-foreground">({msg.retry_count}x denendi)</span>
                            )}
                          </div>
                          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{msg.message_content}</p>
                          {msg.error_message && (
                            <p className="mt-1 text-xs text-destructive flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3 shrink-0" />{msg.error_message}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">{formatDate(msg.created_at)}</span>
                          <div className="flex items-center gap-1">
                            {(msg.status === 'failed' || msg.status === 'pending') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-[10px] gap-1 text-amber-700 hover:bg-amber-100"
                                onClick={() => handleRetryOne(msg.id)}
                                data-testid={`retry-btn-${msg.id}`}
                              >
                                <RefreshCw className="h-3 w-3" />Tekrar
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-1.5 text-destructive hover:bg-destructive/10"
                              onClick={() => handleDeleteMessage(msg.id)}
                              data-testid={`delete-msg-${msg.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
